import { useState, useEffect } from 'react';
import {
  useRecordContext,
  SimpleShowLayout,
  Labeled,
  useInput,
} from 'react-admin';
import { useNavigate } from 'react-router-dom';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Link,
  Chip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import type { Variant } from '@src/types';
import { DashboardResources } from '@src/types/constants';
import { ImageUpload } from '../shared';

// Component for a single variant group (colorway) with image upload
const VariantGroupItem = ({ variant, onImagesChange, initialImages }: {
  variant: Variant;
  onImagesChange: (colorCode: string, updatedImages: Record<string, string>, files: Record<string, File>, keys: string[]) => void;
  initialImages: Record<string, string>;
}) => {
  const [images, setImages] = useState<Record<string, string>>({});
  const navigate = useNavigate();

  // Initialize images from initialImages prop (which comes from record.imagesByColorway or variant.partnerImageUrls)
  useEffect(() => {
    setImages(initialImages);
  }, [initialImages]);

  const handleImagesChange = (
    updatedImages: Record<string, string>,
    files: Record<string, File>,
    keys: string[]
  ) => {
    setImages(updatedImages);
    onImagesChange(variant.colorCode, updatedImages, files, keys);
  };

  return (
    <Accordion sx={{ mb: 1 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
            {variant.colorDisplay}
          </Typography>
          <Chip
            label={`${variant.variantCount || variant.sizeDisplays?.length || 0} variants`}
            size="small"
          />
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Box>
          {/* SKU List */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              SKUs in this color:
            </Typography>
            {variant.sizeDisplays && variant.skus ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {variant.sizeDisplays.map((size: string, index: number) => {
                  const sku = variant.skus[index];
                  if (!sku) return null;
                  return (
                    <Box key={sku} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body2">
                        {size}
                        :
                        {' '}
                        {sku}
                      </Typography>
                      <Link
                        component="button"
                        variant="caption"
                        type="button"
                        onClick={(e: React.MouseEvent) => {
                          e.preventDefault();
                          e.stopPropagation();
                          void navigate(`/${DashboardResources.VariantSkus}/${sku}`);
                        }}
                        sx={{ cursor: 'pointer' }}
                      >
                        (Edit)
                      </Link>
                    </Box>
                  );
                })}
              </Box>
            ) : (
              <Typography variant="body2">No SKU data available</Typography>
            )}
          </Box>

          {/* Image Upload */}
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Upload and manage images for this colorway. Images will apply to all SKUs in this color.
            </Typography>
            <ImageUpload
              images={images}
              onImagesChange={handleImagesChange}
              maxFileSize={10}
              allowRename={true}
            />
          </Box>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

const VariantsSection = () => {
  const record = useRecordContext();
  const { field: imageField } = useInput({ source: 'imagesByColorway' });
  const { field: filesField } = useInput({ source: 'files' });
  const { field: imageKeysField } = useInput({ source: 'imageKeysByColorway' });

  if (!record?.variants || !Array.isArray(record.variants)) {
    return (
      <SimpleShowLayout>
        <Labeled label="">
          <span>No variants available</span>
        </Labeled>
      </SimpleShowLayout>
    );
  }

  const handleImagesChange = (
    colorCode: string,
    updatedImages: Record<string, string>,
    files: Record<string, File>,
    keys: string[]
  ) => {
    // Build the imageKeysByColorway structure
    const currentImagesByColorway = record.imagesByColorway || {};
    const updatedImagesByColorway = {
      ...currentImagesByColorway,
      [colorCode]: updatedImages
    };

    // Build files structure: flatten to colorway_imageKey -> File
    // First, remove all old files for this colorway
    const currentFiles = (record.files || {}) as Record<string, File>;
    const updatedFiles: Record<string, File> = {};

    // Keep files from other colorways
    Object.entries(currentFiles).forEach(([key, file]) => {
      if (!key.startsWith(`${colorCode}_`)) {
        updatedFiles[key] = file;
      }
    });

    // Add new/updated files for this colorway
    Object.entries(files).forEach(([key, file]) => {
      updatedFiles[`${colorCode}_${key}`] = file;
    });

    // Build imageKeysByColorway
    const currentImageKeysByColorway = record.imageKeysByColorway || {};
    const updatedImageKeysByColorway = {
      ...currentImageKeysByColorway,
      [colorCode]: keys
    };

    // Update record
    record.imagesByColorway = updatedImagesByColorway;
    record.files = updatedFiles;
    record.imageKeysByColorway = updatedImageKeysByColorway;

    // Mark form as dirty by updating all three fields
    imageField.onChange(updatedImagesByColorway);
    filesField.onChange(updatedFiles);
    imageKeysField.onChange(updatedImageKeysByColorway);
  };

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Expand each color variant to manage images and view SKUs
      </Typography>
      {record.variants.map((variant: Variant, index: number) => {
        // Get images from imagesByColorway (edited state) or fall back to variant's partnerImageUrls (initial state)
        const colorCode = variant.colorCode || `variant-${index}`;
        const initialImages = record.imagesByColorway?.[colorCode] || variant.partnerImageUrls || {};

        return (
          <VariantGroupItem
            key={colorCode}
            variant={variant}
            onImagesChange={handleImagesChange}
            initialImages={initialImages}
          />
        );
      })}
    </Box>
  );
};

export default VariantsSection;
